import { useState } from 'react';

import { correctingTrailingNumbers } from '@cryptoboyio/mercuryo-lab/utils';
import { AxiosResponse } from 'axios';

import { useDebounce } from '@utils/hooks';

import { BuyTypes, GlobalTypes, useStore } from '@modules';

import useConverterState, { RateType } from './useConverterState';

const GET_RATE_DEBOUNCE_TIME = 500;

export type ErrorDataType = {
  [k in GlobalTypes.CurrencyEnum]?: {
    min?: string;
    max?: string;
  };
};

export type ErrorType = {
  message?: string;
  data: ErrorDataType | ErrorDataType[];
};

export type GetRate = (
  from: GlobalTypes.CurrencyEnum,
  to: GlobalTypes.CurrencyEnum,
  amount: string
) => Promise<AxiosResponse<RateType>>;

export type GetError = (currency: GlobalTypes.CurrencyEnum) => string;

export type Convert = (
  from: BuyTypes.GETBuyRateParams['from'],
  to: BuyTypes.GETBuyRateParams['to'],
  amount: BuyTypes.GETBuyRateParams['amount']
) => void;

export type UseConvert = (
  getRate?: GetRate,
  setRate?: ReturnType<typeof useConverterState>['setRate'],
  setIsFetching?: (value: boolean) => void
) => [Convert, string, () => void];

const useConvert: UseConvert = (getRate, setRate, setIsFetching) => {
  const [error, setError] = useState('');
  const debounced = useDebounce(GET_RATE_DEBOUNCE_TIME);

  const [store] = useStore();
  const { display_options } = store.global.currencies.config;

  const convert: Convert = (from, to, amount) => {
    const displayDigits = display_options?.[from]?.display_digits;
    const totalDigits = display_options?.[from]?.total_digits;

    setError('');
    if (+amount > 0 && getRate) {
      setIsFetching?.(true);
      debounced(() => {
        const correctingAmount = correctingTrailingNumbers(amount, displayDigits, totalDigits);

        getRate(from, to, correctingAmount)
          .then(res => {
            if (res) {
              setError('');
              setRate?.(res.data);
            }
          })
          .catch((e: ErrorType) => {
            const errorData = e?.data?.[from] || e?.data?.[0]?.[from];
            const min = errorData?.min || '0';
            const max = errorData?.max || '0';
            const minAmount = correctingTrailingNumbers(min, displayDigits, totalDigits);
            const maxAmount = correctingTrailingNumbers(max, displayDigits, totalDigits);
            const message = e?.message ? `${e.message} ` : '';
            const minError = minAmount ? `minimum ${minAmount}` : '';
            const maxError = maxAmount ? `maximum ${maxAmount}` : '';
            const prefix = minError || maxError ? `${from}: ` : '';
            const divider = minError && maxError ? `, ` : '';
            setError(`${message}${prefix}${minError}${divider}${maxError}`);
            setRate?.(undefined);
          })
          .finally(() => {
            setIsFetching?.(false);
          });
      });
    }
  };

  return [convert, error, debounced.cancel];
};

export default useConvert;
